import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { PollDto } from "@/api/types";
import type { FormDocument } from "@/components/DocumentInput/useDocumentInput";
import { Form } from "@/components/Form/Form";
import { FormDocumentPicker } from "@/components/Form/FormDocumentPicker";
import { FormImagePicker } from "@/components/Form/FormImagePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { FormVideoPicker } from "@/components/Form/FormVideoPicker";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { PollPreview } from "@/components/Poll/PollPreview";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { MAX_AMOUNT_IMAGES_PER_POST } from "@/helpers/community-feed";
import {
  createDocumentSizeRule,
  createDocumentTypeRule,
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
  createVideoSizeRule,
  createVideoTypeRule,
} from "@/helpers/rules";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { pollMutations } from "@/queries/polls/mutations";
import { useTranslation } from "@/translations";

import { useFlashToast } from "../FlashToast/FlashToast";
import type { PollData } from "../Poll/EditPollModal";

export interface CommunityPollEditFormValues {
  title: string;
  description: string;
  images: FormImage[];
  videos: FormVideo[];
  documents: FormDocument[];
  poll: Pick<PollData, "question" | "options">;
}

interface CommunityPollEditFormProps {
  poll: PollDto;
  onEdit: () => void;
  onCancel: () => void;
}

export function CommunityPollEditForm({ poll, onEdit, onCancel }: CommunityPollEditFormProps): React.ReactNode {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const postHog = usePostHog();
  const showFlashToast = useFlashToast();

  const form = useForm<CommunityPollEditFormValues>({
    defaultValues: {
      title: poll.title,
      description: poll.description ?? "",
      images: poll.images || [],
      videos: poll.videos || [],
      documents: poll.documents || [],
      poll: {
        question: poll.question,
        options: poll.answers.map((a) => a.text),
      },
    },
  });

  const currImages = useWatch({ control: form.control, name: "images" });
  const currVideos = useWatch({ control: form.control, name: "videos" });
  const currDocuments = useWatch({ control: form.control, name: "documents" });
  const currPoll = useWatch({ control: form.control, name: "poll" });

  const isAnyAttachmentSelected = currImages.length > 0 || currVideos.length > 0 || currDocuments.length > 0;
  const attachmentError =
    form.formState.errors["images"]?.message ||
    form.formState.errors["videos"]?.message ||
    form.formState.errors["documents"]?.message;

  const { uploadFormImage } = useUploadImage();
  const { uploadFormDocument } = useUploadDocument();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});
  const updatePollMutation = pollMutations.useUpdatePoll();

  const handleSubmit = async (formValues: CommunityPollEditFormValues) => {
    setIsSubmitting(true);

    const imageUploadPromises = formValues.images.map((image) => uploadFormImage(image));
    const documentUploadPromises = formValues.documents.map((document) => uploadFormDocument(document));
    const videoUploadPromises = formValues.videos.map((video) => uploadFormVideo(video));

    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const documentIds = (await Promise.allSettled(documentUploadPromises))
      .map((document) => (document.status === "fulfilled" ? document.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    try {
      if (
        imageIds.length === formValues.images.length &&
        documentIds.length === formValues.documents.length &&
        videoIds.length === formValues.videos.length
      ) {
        const answers = formValues.poll.options.map((text, index) => ({
          id: poll.answers[index]?.id ?? crypto.randomUUID(),
          text,
        }));

        await updatePollMutation.mutateAsync({
          pollId: poll.id,
          data: {
            title: formValues.title,
            description: formValues.description,
            question: formValues.poll.question,
            answers,
            imageIds,
            videoIds,
            documentIds,
          },
        });

        onEdit();
      } else {
        showFlashToast({
          variant: "alert",
          message: t("page.message-feed.edit.poll.notifications.error"),
        });

        throw new Error("Failed to upload all attachments");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    abortUploadVideos(form.getValues("videos"));
    onCancel();
  };

  return (
    <Form.Root formMethods={form} onSubmit={handleSubmit}>
      <Form.Body>
        <FormField label={t("page.message-feed.resident-create-post.form.title.title")} required>
          <FormInput<CommunityPollEditFormValues>
            name="title"
            placeholder={t("page.message-feed.create.title.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.message-feed.create.title"),
              },
            }}
          />
        </FormField>
        <FormField label={t("page.message-feed.resident-create-post.form.content.title")}>
          <FormTextArea<CommunityPollEditFormValues>
            maxHeight="editor"
            name="description"
            placeholder={t("page.message-feed.create.body.placeholder")}
          />
        </FormField>
        <FormField label={t("page.create-post.form.file")} hideLabel>
          <Stack gap="xs">
            <Inline gap="sm" align="start">
              {(!isAnyAttachmentSelected || currImages.length > 0) && (
                <FormImagePicker<CommunityPollEditFormValues, "images">
                  name="images"
                  maxFiles={MAX_AMOUNT_IMAGES_PER_POST}
                  disabled={currImages.length >= MAX_AMOUNT_IMAGES_PER_POST}
                  rules={{
                    validate: {
                      size: createImageSizeRule({
                        t,
                        onError: () => postHog.capture("form_error_image_size_exceeded"),
                      }),
                      type: createImageTypeRule({ t }),
                    },
                  }}
                />
              )}
              {(!isAnyAttachmentSelected || currVideos.length > 0) && (
                <FormVideoPicker<CommunityPollEditFormValues, "videos">
                  name="videos"
                  maxFiles={1}
                  disabled={currVideos.length > 0}
                  rules={{
                    validate: {
                      size: createVideoSizeRule({
                        t,
                        onError: () => postHog.capture("form_error_video_size_exceeded"),
                      }),
                      type: createVideoTypeRule({ t }),
                    },
                  }}
                />
              )}
              {(!isAnyAttachmentSelected || currDocuments.length > 0) && (
                <FormDocumentPicker<CommunityPollEditFormValues, "documents">
                  name="documents"
                  maxFiles={1}
                  disabled={currDocuments.length > 0}
                  rules={{
                    validate: {
                      size: createDocumentSizeRule({
                        t,
                        onError: () => postHog.capture("form_error_document_size_exceed"),
                      }),
                      type: createDocumentTypeRule({ t }),
                    },
                  }}
                />
              )}
            </Inline>
            {Boolean(attachmentError) && (
              <span
                data-testid="form-input-error"
                role="alert"
                className="text-overline whitespace-nowrap text-text-danger-secondary"
              >
                {attachmentError}
              </span>
            )}
          </Stack>
        </FormField>
        <PollPreview
          poll={{
            ...currPoll,
            allowMultipleAnswers: poll.allowMultipleAnswers,
            durationInDays: 7, // Duration cannot be edited after poll creation, default to 7 days for preview
          }}
          onEdit={(updatedPoll) => form.setValue("poll", updatedPoll, { shouldDirty: true })}
          limitedEditing
        />
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button variant="filled" type="submit" data-testid="edit-submit-btn" isLoading={isSubmitting}>
            {t("common.action.save")}
          </Button>
        )}
        renderSecondaryAction={() => (
          <Button variant="subtle" tone="error" onClick={handleCancel}>
            {t("common.action.cancel")}
          </Button>
        )}
      />
    </Form.Root>
  );
}
